import { Injectable, signal } from '@angular/core';
import { environment } from 'src/environments/environment';

const SIMULATE_KEY = 'simulateNoStripeAccount';

/** Drives the "no Stripe account" top banner shown on pages that require a Stripe account. */
@Injectable({ providedIn: 'root' })
export class PaymentBannerService {
  readonly noStripeAccount = signal(false);
  /** Custom banner message; null means the layout shows its default text. */
  readonly message = signal<string | null>(null);
  private simulationLogged = false;

  constructor() {
    this.syncSimulationFromUrl();
  }

  show(message?: string): void {
    this.noStripeAccount.set(true);
    this.message.set(message ?? null);
  }

  hide(): void {
    this.noStripeAccount.set(false);
    this.message.set(null);
  }

  /** Dev-only: true when not production and localStorage `simulateNoStripeAccount` is 'true'. */
  isSimulatingEmpty(): boolean {
    if (environment.production) return false;
    try {
      return localStorage.getItem(SIMULATE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  /** Reads `?simulateNoStripe=1|true|0|false` and sets/clears the localStorage flag. */
  syncSimulationFromUrl(): void {
    if (environment.production || typeof window === 'undefined') return;
    try {
      const value = new URLSearchParams(window.location.search).get('simulateNoStripe');
      if (value === '1' || value === 'true') localStorage.setItem(SIMULATE_KEY, 'true');
      else if (value === '0' || value === 'false') localStorage.removeItem(SIMULATE_KEY);
    } catch {
      /* storage unavailable */
    }
  }

  /** Returns the response with an empty `data` array while simulating; otherwise unchanged. */
  applySimulation<T extends { data?: unknown[] }>(res: T): T {
    if (!this.isSimulatingEmpty()) return res;
    if (!this.simulationLogged) {
      this.simulationLogged = true;
      console.info('[PaymentBanner] simulating empty Stripe accounts');
    }
    return { ...res, data: [] };
  }
}
